<script>
import { defineComponent, nextTick } from 'vue'
import BaseResource from '@sigmacloud/sigma-client/dist/resources'

// ag-grid cell editor. defineComponent (not Vue.extend) to drop the GLOBAL_EXTEND path; the legacy editor
// instance API (getValue, etc.) is unchanged — the grid stays in non-reactive custom-component mode.
export default defineComponent({
  name: 'GridAutocomplete',

  props: {
    // number of keystrokes to send lookup
    keyThreshold: {
      required: false,
      type: Number,
      default: 1
    }
  },

  data () {
    return {
      keyword: '', // The actual input field
      selection: undefined, // The Selected Value

      isSelected: false, // If the user has made a selection
      isError: false, // If the input or selected field is incorrect, show an error
      showList: false, // Show the options list
      highlightedPosition: 0, // Current list item to highlight
      query: '', // Search sent to Rest Resource API
      isLoading: false,
      resources: [], // Resources retrieved from search

      // Ag-Grid Vue components can't get props this way so we are going to look at the grid params.options object that we need to pass
      lineResource: undefined, // Should be a Rest Resource object from params.data
      resourceClass: undefined, // Should be a Rest Resource
      options: [], // Should be a list of resources
      name: '', // Name of input field.  Not really necessary, but helpful if we use the form object down the road
      displayProp: 'code', // Properties to display after a selection is made, e.g. name.
      classes: 'form-control', // A list of css classes
      placeholder: '', // Placeholder text for input
      required: false, // To mark this field as required
      readonly: false, // To mark this field as readonly/disabled
      primaryFilter: undefined, // Primary filter to be used with resourceClass, e.g. ?filter=
      filterOptions: {} // Filter options that are not the primary filter.  Helps with limiting options, e.g. { type__in: 'POSTED', 'CLOSED' }
    }
  },

  computed: {
    // ag-grid passes this natively so if there, use it.
    gridParams () {
      // @ts-ignore
      return this.params || {}
    },
    // ag-grid allows the passing of custom parameter properties into ICellEditorParams. No need to create interfaces for these
    paramOptions () {
      // @ts-ignore
      return this.gridParams.options || {}
    },
    // Matches to our search
    optionMatches () {
      // eslint-disable-next-line
      const keyword = this.keyword.replace(/[|()\[\]\\]/g, '') || undefined

      if (keyword && this.resources) {
        let matches = this.resources.map((resource) => {
          return {
            name: (resource.attributes || {}).name || (resource.attributes || {}).description,
            code: (resource.attributes || {}).code,
            id: resource.id || resource.uuid
          }
        })
        // console.log(matches)
        return matches
      } else {
        return []
      }
    }
  },
  methods: {
    // This is what goes back to ag-grid
    getValue () {
      if (this.selection && this.resources.length) {
        let result = this.resources.find((r) => r.id === this.selection.id)
        return result
      } else {
        return
      }
    },

    // Every time the input field is updated
    onInput (value) {
      // console.log('onInput: ' + value.target.value)
      if (!value) {
        return
      }

      if (this.keyword.length >= this.keyThreshold) this.lookup()
      this.isSelected = false
      this.isError = false
      this.showList = !!this.keyword
    },

    // When leaving the input field, select the highlighted value
    async onSelect () {
      const choice = this.optionMatches[this.highlightedPosition]
      this.showList = false

      // Make sure it's a valid entry with a displayable property
      if (!this.keyword && this.required) {
        this.isError = true
        this.$emit('error', 'No selection made.')
        return
      } else if (!this.keyword) {
        // Return empty if no value and tabbing through.
        return
      } else if (choice && this.resources) {
        this.selection = choice
        this.isSelected = true
      } else if (choice && choice.hasOwnProperty(this.displayProp)) {
        this.selection = choice
        this.isSelected = true
      } else if (this.isSelected) {
        // Do nothing if already selected and tabbing through.
      } else {
        this.isError = true
        this.$emit('error', choice)
        return
      }
      // need this since ag-grid getters and setters are not async by default
      if (this.selection && this.paramOptions.updateLineResource) {
        try {
          this.lineResource.set(this.gridParams.colDef.field, this.selection.id)
          await this.lineResource.resolveRelated()
        } catch (e) {
          console.log(e)
        }
      } else if (!this.selection) {
        this.isError = true
        this.$emit('error', choice)
        return
      }
      this.gridParams.api.stopEditing()
      this.$emit('select', this.selection)
      return
    },

    onClick () {
      this.onSelect()
    },

    onKeyDown (event) {
      let keyCode = event.which || event.keyCode
      // console.log(keyCode)

      if (keyCode === 13 || keyCode === 9) {
        this.onSelect() // Enter/Return
      } else if (keyCode === 38) {
        this.moveUp() // Up
      } else if (keyCode === 40) {
        this.moveDown() // Down
      } else if (keyCode === 192) {
        // Escape
        this.showList = false
        this.$emit('select', this.selection)
      } else {
        // No action for now
      }

      // Recommended in ag-grid documentation
      event.stopPropagation()
    },

    // Rest resource lookup
    async lookup () {
      let ResourceClass = (this.resourceClass) || undefined
      this.isLoading = true

      if (this.keyword) {
        let query = Object.assign({}, this.filterOptions)
        query.search = this.keyword
        let result = await ResourceClass.filter(query)
        this.resources = result.resources
      } else {
        let result = await ResourceClass.list()
        this.resources = result.resources
      }

      this.isLoading = false
    },

    // Rest resource get detail
    detailById (id, number) {
      let ResourceClass = (this.resourceClass) || undefined

      if (id) {
        return ResourceClass.detail(id)
      } else {
        return new Promise((resolve) => resolve(false))
      }
    },

    // Move the highlighed item index up
    moveUp () {
      // Only scroll down if not past the last item on the list
      if (this.showList && this.optionMatches.length && this.highlightedPosition > 0) {
        this.highlightedPosition--

        // Else scroll to the bottom
      } else if (this.showList && this.optionMatches.length && this.highlightedPosition <= 0) {
        this.highlightedPosition = this.optionMatches.length - 1
      } else {
        return false
      }

      let field = this.$refs['list-items']
      field[this.highlightedPosition].scrollIntoView()
      field[this.highlightedPosition].focus()
      return true
    },

    // Move the highlighed item index down
    moveDown () {
      // Only scroll down if not past the last item on the list
      if (this.showList && this.optionMatches.length && this.highlightedPosition < this.optionMatches.length - 1) {
        this.highlightedPosition++

        // Else scroll to the top
      } else if (this.showList && this.optionMatches.length) {
        this.highlightedPosition = 0
      } else {
        return false
      }

      let field = this.$refs['list-items']
      field[this.highlightedPosition].scrollIntoView()
      field[this.highlightedPosition].focus()
      return true
    }
  },

  mounted () {
    if ((this.gridParams.charPress || this.gridParams.value) && this.keyword === '') {
      this.keyword = this.gridParams.charPress || this.gridParams.value
    }
    if (this.paramOptions.filterOptions) {
      this.filterOptions = this.paramOptions.filterOptions
    }

    this.resourceClass = this.paramOptions.resourceClass || BaseResource
    this.lineResource = this.gridParams.data || {}
    this.name = this.gridParams.colDef.field || 'autocomplete-field'
    nextTick(() => {
      let field = this.$refs['input']
      field.focus()
    })
  }
})
</script>

<template>
<div :ref="'container'" class="mood" tabindex="0">
  <input
    ref="input"
    :class="[{ 'form-error': isError }, classes]"
    :name="name + Math.random() * 1000000 + 1"
    :placeholder="placeholder"
    :required="required"
    :readonly="readonly"
    :autocomplete="'force_off_' + Math.random() * 1000000 + 1"
    v-model="keyword"
    @input="onInput"
    @focus="onInput"
    @blur="onSelect"
    @keydown="onKeyDown"
  />
  <ul v-show="showList" class="position-fixed options-list" ref="list">
    <li
      v-for="(option, index) in optionMatches"
      :key="index"
      :class="{ highlighted: index === highlightedPosition }"
      ref="list-items"
      @mouseleave="highlightedPosition = -1"
      @mouseenter="highlightedPosition = index"
      @mousedown="onSelect"
    >
      <slot style="vertical-align: middle;" name="item" :code="option.code" :itemname="option.name" :item="option">{{ option.code + ' - ' + option.name }}</slot>
    </li>
  </ul>
</div>
</template>

<style scoped>
.form-inline .form-control {
  width: 100%;
}
.options-list {
  /* list-style-type: none; */
  border: 1px solid #dbdbdb;
  width: 20%;
  min-width: 250px;
  max-width: 500px;
  min-height: 20px;
  max-height: 500px;
  overflow: scroll;
  margin-top: 5px;
  padding-inline-start: 0px;
  line-height: 2em;
  vertical-align: middle;
  background: white;
  z-index: 1060;
}
.options-list li {
  width: 100%;
  flex-wrap: wrap;
  margin: 0px;
  padding-left: 10px;
  border-bottom: 1px solid #eee;
  color: rgb(24, 37, 79);
  cursor: pointer;
  vertical-align: middle;
}
.options-list li.highlighted {
  background: #feddbb;
}
.form-error,
.form-error2 {
  background: yellow !important;
  opacity: 0.7;
  font-weight: bold;
  color: #cc0000 !important;
}
</style>
